import React, { useContext } from 'react';
import { Layout, Menu, Button, Typography } from 'antd';
import {
  HomeOutlined,
  TeamOutlined,
  CarOutlined,
  ApiOutlined,
  ShopOutlined,
  LogoutOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { AuthContext } from '../contexts/AuthContext';

const { Header, Content, Sider } = Layout;
const { Title } = Typography;
const { SubMenu } = Menu;

/**
 * Main application layout wrapper with sidebar navigation and header
 * Provides role-based navigation and user authentication display
 */
export default function LayoutWrapper({ children }) {
  const { currentUser, userRole, userName } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Handles user logout by signing out from Firebase and redirecting to login
   */
  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  /**
   * Determines which menu item should be highlighted based on current route
   * @returns {string} The key of the selected menu item
   */
  const getSelectedKey = () => {
    const path = location.pathname;
    
    // Dashboard routes
    if (path === '/' || path === '/procurement') return 'procurement';
    if (path === '/transporter') return 'transporter';
    
    // User management routes
    if (path.startsWith('/users')) return 'users';
    if (path.startsWith('/items')) return 'items';
    if (path.startsWith('/saved-points')) return 'saved-points';
    
    // Transporter routes
    if (path.startsWith('/transporters')) return 'transporters';
    if (path.startsWith('/bids')) return 'bids';
    if (path.startsWith('/deals')) return 'deals';
    
    // Procurement routes
    if (path.startsWith('/vendors')) return 'vendors';
    if (path.startsWith('/procurement-bids')) return 'procurement-bids';
    if (path.startsWith('/procurement-deals')) return 'procurement-deals';
    if (path.startsWith('/dispatch')) return 'dispatch';
    if (path.startsWith('/onboarding')) return 'onboarding';
    
    return 'procurement'; // Default fallback
  };

  // Submenus that should be open by default
  const defaultOpenKeys = [
    'settingsMenu',
    'dashboardGroup',
    'transporterGroup',
    'procurementGroup'
  ];

  // Custom CSS for sidebar styling
  const sidebarCss = `
    .layout-wrapper .ant-layout-sider {
      background: #FFFFFF !important;
      border-right: 1px solid #E8E8E8;
    }
    .layout-wrapper .ant-menu {
      background: transparent !important;
      border-right: none !important;
    }
    .layout-wrapper .ant-menu-item,
    .layout-wrapper .ant-menu-submenu-title {
      color: #8C8C8C !important;
    }
    .layout-wrapper .ant-menu-item:hover,
    .layout-wrapper .ant-menu-submenu-title:hover {
      background: #F5F5F5 !important;
    }
    .layout-wrapper .ant-menu-item-selected,
    .layout-wrapper .ant-menu-submenu-title-selected {
      background: #E8E8E8 !important;
      color: #262626 !important;
    }
    .layout-wrapper .ant-menu-item-selected .anticon,
    .layout-wrapper .ant-menu-submenu-title-selected .anticon {
      color: #262626 !important;
    }
    .layout-wrapper .ant-menu-item .anticon,
    .layout-wrapper .ant-menu-submenu-title .anticon {
      color: #8C8C8C !important;
    }
    .logo-container {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 20px 16px;
      border-bottom: 1px solid #E8E8E8;
      background: linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%);
      margin-bottom: 8px;
    }
    .logo-image {
      width: 40px;
      height: 40px;
      object-fit: contain;
    }
    .logo-text {
      color: #FFFFFF !important;
      margin: 0 !important;
      font-weight: 600;
      text-shadow: 0 1px 2px rgba(0,0,0,0.1);
    }
  `;

  return (
    <>
      <style>{sidebarCss}</style>

      <Layout className="layout-wrapper" style={{ minHeight: '100vh', background: '#F5F7FA' }}>
        {/* Sidebar Navigation */}
        <Sider breakpoint="lg" collapsedWidth="0">
          {/* App Logo/Title */}
          <div className="logo-container">
            <img 
              src="/logo.png" 
              alt="SkyQuote Logo" 
              className="logo-image"
            />
            <Title level={3} className="logo-text">
              SkyQuote
            </Title>
          </div>

          {/* Navigation Menu */}
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={[getSelectedKey()]}
            defaultOpenKeys={defaultOpenKeys}
          >
            {/* Admin Dashboard - Full access to all features */}
            {userRole === 'admin' && (
              <SubMenu key="dashboardGroup" icon={<HomeOutlined />} title="Dashboard">
                <Menu.Item key="procurement" icon={<HomeOutlined />}>
                  <Link to="/">Procurement Dashboard</Link>
                </Menu.Item>
                <Menu.Item key="transporter" icon={<CarOutlined />}>
                  <Link to="/transporter">Transporters Dashboard</Link>
                </Menu.Item>
              </SubMenu>
            )}

            {/* Admin Menu Items - Complete access */}
            {userRole === 'admin' && (
              <>
                {/* User and Item Management */}
                <Menu.Item key="users" icon={<TeamOutlined />}>
                  <Link to="/users">Users</Link>
                </Menu.Item>
                <Menu.Item key="items" icon={<ApiOutlined />}>
                  <Link to="/items">Items</Link>
                </Menu.Item>
                <Menu.Item key="saved-points" icon={<EnvironmentOutlined />}>
                  <Link to="/saved-points">Saved Points</Link>
                </Menu.Item>

                {/* Transporter Management Section */}
                <SubMenu key="transporterGroup" title="Transporter">
                  <Menu.Item key="transporters" icon={<CarOutlined />}>
                    <Link to="/transporters">Transporters</Link>
                  </Menu.Item>
                  <Menu.Item key="bids" icon={<ApiOutlined />}>
                    <Link to="/bids">Bids</Link>
                  </Menu.Item>
                  <Menu.Item key="deals" icon={<ShopOutlined />}>
                    <Link to="/deals">Deals</Link>
                  </Menu.Item>
                </SubMenu>

                {/* Procurement Management Section */}
                <SubMenu key="procurementGroup" title="Procurement">
                  <Menu.Item key="vendors" icon={<ShopOutlined />}>
                    <Link to="/vendors">Vendors</Link>
                  </Menu.Item>
                  <Menu.Item key="onboarding" icon={<TeamOutlined />}>
                    <Link to="/onboarding">Onboarding</Link>
                  </Menu.Item>
                  <Menu.Item key="procurement-bids" icon={<ApiOutlined />}>
                    <Link to="/procurement-bids">Bids</Link>
                  </Menu.Item>
                  <Menu.Item key="procurement-deals" icon={<ShopOutlined />}>
                    <Link to="/procurement-deals">Deals</Link>
                  </Menu.Item>
                  <Menu.Item key="dispatch" icon={<CarOutlined />}>
                    <Link to="/dispatch">Dispatch</Link>
                  </Menu.Item>
                </SubMenu>
              </>
            )}

            {/* Transporter Manager - Items + Transporter management */}
            {userRole === 'manager' && (
              <>
                <Menu.Item key="items" icon={<ApiOutlined />}>
                  <Link to="/items">Items</Link>
                </Menu.Item>
                <Menu.Item key="saved-points" icon={<EnvironmentOutlined />}>
                  <Link to="/saved-points">Saved Points</Link>
                </Menu.Item>

                {/* Transporter Section */}
                <SubMenu key="transporterGroup" title="Transporter">
                  <Menu.Item key="transporters" icon={<CarOutlined />}>
                    <Link to="/transporters">Transporters</Link>
                  </Menu.Item>
                  <Menu.Item key="bids" icon={<ApiOutlined />}>
                    <Link to="/bids">Bids</Link>
                  </Menu.Item>
                  <Menu.Item key="deals" icon={<ShopOutlined />}>
                    <Link to="/deals">Deals</Link>
                  </Menu.Item>
                </SubMenu>
              </>
            )}

            {/* Transporter Staff - Basic transporter operations */}
            {userRole === 'staff_transport' && (
              <>
              <Menu.Item key="items" icon={<ApiOutlined />}>
                  <Link to="/items">Items</Link>
                </Menu.Item>
                <Menu.Item key="saved-points" icon={<EnvironmentOutlined />}>
                  <Link to="/saved-points">Saved Points</Link>
                </Menu.Item>
                <SubMenu key="transporterGroup" title="Transporter">
                <Menu.Item key="bids" icon={<ApiOutlined />}>
                  <Link to="/bids">Bids</Link>
                </Menu.Item>
                <Menu.Item key="deals" icon={<ShopOutlined />}>
                  <Link to="/deals">Deals</Link>
                </Menu.Item>
              </SubMenu>
                </>
              
            )}

            {/* Procurement Manager - Items + Procurement management */}
            {userRole === 'manager_procurement' && (
              <>
                <Menu.Item key="items" icon={<ApiOutlined />}>
                  <Link to="/items">Items</Link>
                </Menu.Item>
                <Menu.Item key="saved-points" icon={<EnvironmentOutlined />}>
                  <Link to="/saved-points">Saved Points</Link>
                </Menu.Item>
                
                {/* Procurement Section */}
                <SubMenu key="procurementGroup" title="Procurement">
                  <Menu.Item key="vendors" icon={<ShopOutlined />}>
                    <Link to="/vendors">Vendors</Link>
                  </Menu.Item>
                  <Menu.Item key="onboarding" icon={<TeamOutlined />}>
                    <Link to="/onboarding">Onboarding</Link>
                  </Menu.Item>
                  <Menu.Item key="procurement-bids" icon={<ApiOutlined />}>
                    <Link to="/procurement-bids">Bids</Link>
                  </Menu.Item>
                  <Menu.Item key="procurement-deals" icon={<ShopOutlined />}>
                    <Link to="/procurement-deals">Deals</Link>
                  </Menu.Item>
                  <Menu.Item key="dispatch" icon={<CarOutlined />}>
                    <Link to="/dispatch">Dispatch</Link>
                  </Menu.Item>
                </SubMenu>
              </>
            )}

            {/* Procurement Staff - Basic procurement operations */}
            {userRole === 'staff_procurement' && (
              <SubMenu key="procurementGroup" title="Procurement">
                <Menu.Item key="procurement-bids" icon={<ApiOutlined />}>
                  <Link to="/procurement-bids">Bids</Link>
                </Menu.Item>
                <Menu.Item key="dispatch" icon={<CarOutlined />}>
                  <Link to="/dispatch">Dispatch</Link>
                </Menu.Item>
              </SubMenu>
            )}
          </Menu>
        </Sider>

        {/* Main Content Area */}
        <Layout style={{ background: '#F5F7FA' }}>
          {/* Header with User Info and Logout */}
          <Header style={{ background: '#fff', padding: '0 24px' }}>
            {currentUser && (
              <div style={{ float: 'right', display: 'flex', alignItems: 'center', gap: 16 }}>
                <span style={{ color: '#666', fontSize: 14 }}>
                  {userName || currentUser.email}
                </span>
                <Button icon={<LogoutOutlined />} onClick={handleLogout}>
                  Logout
                </Button>
              </div>
            )}
          </Header>

          {/* Page Content */}
          <Content style={{ margin: '24px 16px', background: '#F5F7FA' }}>
            <div style={{ minHeight: 360 }}>
              {children}
            </div>
          </Content>
        </Layout>
      </Layout>
    </>
  );
}

